import { FC, useEffect, useState } from 'react';
import { CareMapDrawerPage } from '../../components/CareMapDrawerPage';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { HistoryViewModel } from '../../support/HistoryViewModel';
import { ICareMapNoteTarget } from '../model/ICareMapNoteTarget';
import { IAppointment } from '../../appointments/IAppointment';
import { NoteDetailBody } from './NoteDetailBody';

interface IViewNoteUsingCareMapDrawerProps {
    noteId: string;
    defaultNoteTarget?: ICareMapNoteTarget;
}

export const ViewNoteUsingCareMapDrawer: FC<IViewNoteUsingCareMapDrawerProps> = ({
    noteId
}) => {
    const history = useInstance(HistoryViewModel);

    // If the note no longer exists — deleted by us or a collaborator while this drawer page (or the
    // editor we'd return to) was open — don't strand the user on a drawer page explaining a note
    // they were already reading; pop back to the notes list, as if Back were tapped. This also
    // self-heals the editor's onDeleted history.back(), which lands here on the now-deleted note.
    //
    // NoteDetailBody decides WHEN the note is known to be gone, only once the server has confirmed
    // it, so neither an initial deep-link (notes not fetched yet) nor an in-flight targeted fetch
    // navigates away before the note can arrive. The guard here is the other half: only pop when
    // THIS view page is the active route — while the editor is stacked on top (route ends in /edit)
    // this page is still mounted, and a back() from here would pop the editor and pre-empt its
    // "Note deleted" alert.
    //
    // Latched into state rather than acted on directly in the callback, because the two conditions
    // become true in either order: when the note is deleted from under the open editor, it is known
    // to be gone while the route is still /edit, and the pop has to wait for the editor to dismiss.
    // Keeping it in state is what lets this effect re-run on that route change.
    // Keyed by note id, not a bare flag: this component is reused across note ids by the care-map
    // route, and a bare flag would pop the user out of a perfectly good note they navigated to next.
    const [goneNoteId, setGoneNoteId] = useState<string | undefined>();
    const noteIsGone = goneNoteId === noteId;
    const isActiveViewRoute = history.getCurrentLocation().pathname.endsWith(`/notes/${noteId}`);
    useEffect(() => {
        if (noteIsGone && isActiveViewRoute) {
            history.back();
        }
    }, [noteIsGone, isActiveViewRoute]); // eslint-disable-line react-hooks/exhaustive-deps

    // No early return on a missing note: NoteDetailBody renders the resolving and retryable-error
    // states, and short-circuiting above it would leave this surface showing an empty drawer body
    // forever — the very defect the shared body exists to prevent.
    const editHandler = () => {
        const currentPath = history.getCurrentLocation().pathname;
        history.push(`${currentPath}/edit`);
    };

    const handleAppointmentClick = (appointment: IAppointment) => {
        const basePath = history.getCurrentLocation().pathname.split('/').slice(0, -2).join('/');
        history.push(`${basePath}/appointments/${appointment.appointmentId}`);
    };

    return (
        <CareMapDrawerPage
            title="View Note"
            hasBack={true}
            data-type="view-note-caremap-drawer"
        >
            <NoteDetailBody
                noteId={noteId}
                trackingFrom="caremap-drawer"
                editHandler={editHandler}
                onAppointmentClick={handleAppointmentClick}
                onCloseDrawer={() => history.back()}
                onNoteUnavailable={() => setGoneNoteId(noteId)}
            />
        </CareMapDrawerPage>
    );
};
